// src/components/frontpage/columns/SwapColumn.jsx
// Swap & Donate listings for the MultiViewGrid front page column.
// Supports click-through to SwapDetailModal and session-local preview listings.

import React, { useState } from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAuth } from '../../../contexts/AuthContext';
import SwapCreateModal from '../SwapCreateModal';
import ModeColumn from './ModeColumn';

// Inline fallback for listings without images
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='%23e5e7eb'%3E%3Crect width='40' height='40'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='14'%3E%F0%9F%93%A6%3C/text%3E%3C/svg%3E";

const SwapColumn = ({ data = [], onClick, onCreateSwap, hasPreviewItems = false, hideColumnHeader = false, comingSoon = false }) => {
  const theme = useTheme();
  const { t } = useTranslation('frontpage');
  const { requireAuth } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleNewSwap = () => {
    if (comingSoon) return;
    requireAuth(t('ui:login_required'), () => {
      setShowCreateModal(true);
    });
  };

  const handleCreateListing = (newItem) => {
    onCreateSwap?.(newItem);
  };

  return (
    <ModeColumn
      title={t('column_marketplace')}
      themeColor={theme.primary}
      hideColumnHeader={hideColumnHeader}
      onAdd={comingSoon ? undefined : handleNewSwap}
      addLabel={t('new_swap')}
      bodyOpacity={comingSoon ? 0.35 : 1}
      overlay={
        <>
          {/* "Coming Soon" watermark — diagonal text centred over the column */}
          {comingSoon && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 10
              }}
            >
              <span style={{
                transform: 'rotate(-35deg)',
                fontSize: '36px',
                fontWeight: 'bold',
                color: theme.primary,
                opacity: 0.2,
                whiteSpace: 'nowrap',
                letterSpacing: '0.1em',
                fontFamily: 'OpenSauceSans, sans-serif',
                textTransform: 'uppercase',
                userSelect: 'none'
              }}>
                {t('marketplace_coming_soon') || 'Coming Soon'}
              </span>
            </div>
          )}

          {/* Create modal */}
          {!comingSoon && showCreateModal && (
            <SwapCreateModal
              isOpen={showCreateModal}
              onClose={() => setShowCreateModal(false)}
              onCreateListing={handleCreateListing}
            />
          )}
        </>
      }
    >
      {/* Preview mode banner — shown when session-local listings exist */}
        {hasPreviewItems && !comingSoon && (
          <div style={{
            padding: '6px 10px',
            marginBottom: '8px',
            background: '#EFF6FF',
            borderRadius: '6px',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '11px',
            color: '#1E40AF',
            textAlign: 'center'
          }}>
            {t('swap_preview_note')}
          </div>
        )}

        {/* Listing cards */}
        {data.map(item => (
          <div
            key={item.id}
            onClick={() => !comingSoon && onClick?.(item)}
            style={{
              display: 'flex',
              gap: '8px',
              padding: '10px',
              marginBottom: '8px',
              background: item.isPreview ? '#FEFCE8' : 'white',
              borderRadius: '8px',
              minHeight: '50px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              transition: 'box-shadow 0.15s',
              cursor: comingSoon ? 'default' : (onClick ? 'pointer' : 'default'),
              border: item.isPreview ? '1px solid #FDE68A' : 'none'
            }}
            onMouseEnter={(e) => {
              if (!comingSoon && onClick) e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
            }}
          >
            {/* Micro image */}
            <img
              src={item.image_url || FALLBACK_IMAGE}
              alt=""
              loading="lazy"
              style={{
                width: '40px',
                height: '40px',
                objectFit: 'cover',
                borderRadius: '4px',
                flexShrink: 0
              }}
              onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; }}
            />

            {/* Text content */}
            <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              {/* Title */}
              <div style={{
                fontFamily: 'OpenSauceSans, sans-serif',
                fontWeight: '600',
                fontSize: '13px',
                color: '#1f2937',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.title}
              </div>

              {/* Sub-locality + days remaining */}
              <div style={{
                fontFamily: 'OpenSauceSans, sans-serif',
                fontSize: '11px',
                color: '#6b7280',
                marginTop: '2px'
              }}>
                {item.sub_locality} · {item.days_remaining} {t('days_left')}
              </div>
            </div>
          </div>
        ))}
    </ModeColumn>
  );
};

export default SwapColumn;
